import { inject, Injectable } from '@angular/core';
import { AppState } from '../models/app-state.model';
import { ImportExportService } from './import-export.service';
import { EncryptionService } from './encryption.service';
import { SessionVaultService } from './session-vault.service';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly STORAGE_KEY = 'project_planner_state';
  private readonly importExportService = inject(ImportExportService);
  private readonly encryptionService = inject(EncryptionService);
  private readonly sessionVaultService = inject(SessionVaultService);

  constructor() {
    this.sessionVaultService.setHasStoredSnapshot(this.hasStoredState());
  }

  loadState(): unknown | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) {
        return null;
      }

      if (!stored.trim().startsWith('{')) {
        return null;
      }

      return JSON.parse(stored) as unknown;
    } catch (e) {
      console.error('Failed to parse state from localStorage', e);
      return null;
    }
  }

  saveState(state: AppState): void {
    const password = this.sessionVaultService.getEncryptionPassword();

    if (!password) {
      console.warn('Encrypted persistence skipped because no session encryption password is available.');
      return;
    }

    void this.persistEncryptedState(state, password);
  }

  loadEncryptedState(): string | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);

      if (!stored || stored.trim() === '') {
        return null;
      }

      return stored;
    } catch (e) {
      console.error('Failed to read encrypted state from localStorage', e);
      return null;
    }
  }

  saveEncryptedState(encryptedPayload: string): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, encryptedPayload);
      this.sessionVaultService.setHasStoredSnapshot(true);
    } catch (e) {
      console.error('Failed to save encrypted state to localStorage', e);
    }
  }

  clearState(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      this.sessionVaultService.setHasStoredSnapshot(false);
    } catch(e) {
      console.error('Failed to clear state from localStorage', e);
    }
  }

  private hasStoredState(): boolean {
    return this.loadEncryptedState() !== null;
  }

  private async persistEncryptedState(state: AppState, password: string): Promise<void> {
    try {
      const plainJson = this.importExportService.createExportJson(state);
      const encryptedPayload = await this.encryptionService.encryptText(plainJson, password);
      this.saveEncryptedState(encryptedPayload);
    } catch (e) {
      console.error('Failed to save encrypted state to localStorage', e);
    }
  }
}
